import { useState, useEffect } from 'react';
import { cn, getOffset } from '@anytag/shared-utils';
import { FormAction } from '@anytag/shared-types';
import { Header, SelectorField } from '@anytag/shared-molecules';
import { Form, RenderDataWithFallback, SortableList } from '@anytag/shared-atoms';
import {
  useDebounce,
  useQueryHelper,
  useEventListener,
  useAllCountryOptions,
  useUpdateDocumentTitle,
  useParamsSearch
} from '@anytag/shared-hooks';
import {
  MarketplaceForReorder,
  useAllMarketplacesForReorderLazyQuery,
  useReorderMarketplaceCampaignsMutation
} from '@anytag/graphql';
import { CampaignOrderCard } from './campaign-order-card';
import {
  MarketplacesReorderFormKeys,
  MarketplacesReorderFormValues,
  MarketplacesReorderSearchSchemaType
} from './schema-types';

export const MarketplacesReorder = () => {
  useUpdateDocumentTitle({
    href: '/marketplace',
    alwaysBackToParent: true,
    title: 'documentTitle.MarketplaceReorder',
    appHeader: 'appHeader.MarketplaceReorder'
  });
  const { debounce } = useDebounce();
  const [step, setStep] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { t, enqueueSnackbar } = useQueryHelper();
  const { allCountryOptions } = useAllCountryOptions();
  const { limit, filter, setFilter } = useParamsSearch<MarketplacesReorderSearchSchemaType>();
  const [campaigns, setCampaigns] = useState<MarketplaceForReorder[]>([]);
  const { callReorderMarketplaceCampaigns } = useReorderMarketplaceCampaignsMutation();
  const { getAllMarketplacesForReorder, loading, called } = useAllMarketplacesForReorderLazyQuery({
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ allMarketplacesForReorder }) => {
      if ((allMarketplacesForReorder?.length || 0) < limit) {
        setHasMore(false);
      }

      setCampaigns((prevCampaigns) => [...prevCampaigns, ...(allMarketplacesForReorder || [])]);
    }
  });

  const length = campaigns.length;

  const fetchCampaigns = async (nextPage: number) => {
    if (!filter.countryId || !hasMore) {
      return;
    }

    await getAllMarketplacesForReorder({
      variables: { limit, offset: getOffset(nextPage, limit), countryId: filter.countryId }
    });
  };

  useEffect(() => {
    fetchCampaigns(step);
  }, [filter.countryId]);

  useEventListener('scroll', () => {
    if (
      !loading &&
      hasMore &&
      document.scrollingElement?.scrollHeight &&
      window.innerHeight + document.documentElement.scrollTop + 1 >= document.scrollingElement?.scrollHeight
    ) {
      const nextStep = step + 1;
      setStep(nextStep);
      fetchCampaigns(nextStep);
    }
  });

  const handleCountryChange: FormAction<MarketplacesReorderFormValues>['onSubmit'] = ({ countryId }) => {
    // Reset states
    if (countryId !== filter.countryId) {
      setStep(1);
      setCampaigns([]);
      setHasMore(true);
    }

    setFilter({ countryId });
  };

  const handleSubmitReorderCampaigns = async (reorderCampaigns: MarketplaceForReorder[]) => {
    try {
      await callReorderMarketplaceCampaigns({
        variables: { campaignIds: reorderCampaigns.map((campaign) => campaign.id) }
      });
      enqueueSnackbar(t('succeededInSave'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  const handleReorderCampaigns = (reorderCampaigns: MarketplaceForReorder[]) => {
    setCampaigns(reorderCampaigns);
    debounce(() => handleSubmitReorderCampaigns(reorderCampaigns), 3000);
  };

  return (
    <div>
      <Header />
      <div className="box-border rounded-sm bg-white p-6">
        <p className="text-xl font-semibold">{t('Reorder campaigns')}</p>
        <p className="mt-4 text-md">
          {t('You can change the order of the campaigns displayed on the AnyCreator app here')}
        </p>

        <Form.Provider<MarketplacesReorderFormValues>
          onSubmit={handleCountryChange}
          className="mt-4 max-w-114 rounded-none"
          defaultValues={{ countryId: filter.countryId }}
        >
          {({ onSubmit }) => (
            <SelectorField<MarketplacesReorderFormKeys>
              name="countryId"
              onSubmit={onSubmit}
              options={allCountryOptions}
              title={t('Selector.Select country')}
            />
          )}
        </Form.Provider>
      </div>
      {filter.countryId ? (
        <>
          <div className="mt-6 box-border border-t-border px-2">
            <RenderDataWithFallback loading={!called && loading} hasNoData={!length}>
              <SortableList
                items={campaigns}
                onChange={handleReorderCampaigns}
                renderItem={(campaign, index) => (
                  <CampaignOrderCard
                    index={index}
                    data={campaign}
                    key={campaign.id}
                    className={cn({ 'border-b-border': index !== length - 1 })}
                  />
                )}
              />
            </RenderDataWithFallback>
          </div>

          {hasMore && loading && called ? (
            <div className="mt-6 text-center text-base text-tertiary">{t('Loading more campaigns')}</div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};
